import { AuthService } from "../services/auth.service";
import { JwtDTO } from "../models/jwt-dto";
import { catchError, concatMap, timeout } from "rxjs/operators";
import { Inject, Injectable, InjectionToken } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { TokenService } from "../services/token.service";
import { TranslateService } from "@ngx-translate/core";

const AUTHORIZATION = "Authorization";

export const DEFAULT_TIMEOUT = new InjectionToken<number>("defaultTimeout");

@Injectable({
  providedIn: "root",
})
export class ProdInterceptorService implements HttpInterceptor {
  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private translateService: TranslateService,
    @Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.tokenService.isLogged()) {
      return next.handle(req);
    }

    let intReq = req;
    let err = this.checkTokenIsExpired();
    if (err !== null) {
      return throwError(err);
    }
    const token = this.tokenService.getToken();

    intReq = this.addToken(req, token);

    const timeoutValue = req.headers.get("timeout") || this.defaultTimeout;
    const timeoutValueNumeric = Number(timeoutValue);

    return next.handle(intReq).pipe(
      timeout(timeoutValueNumeric),
      catchError((err: HttpErrorResponse) => {
        let forbiddenError = err.headers.get("Forbidden-Error")
          ? err.headers.get("Forbidden-Error")
          : err.error.forbiddenError
          ? err.error.forbiddenError
          : "";
        if (err.status === 401) {
          if (forbiddenError == "EXPIRED") {
            const dto: JwtDTO = new JwtDTO(token);
            return this.authService.refresh(dto).pipe(
              concatMap((data: any) => {
                this.tokenService.setToken(data.token);
                intReq = this.addToken(req, data.token);
                return next.handle(intReq);
              })
            );
          } else {
            let message: string = this.translateService.instant(
              "errorsInterceptor.accessDenied"
            );
            err.error.mensaje = message;
            return throwError(err);
          }
        } else if (forbiddenError == "NOT_ALLOWED") {
          let message: string = this.translateService.instant(
            "errorsInterceptor.accessDenied2"
          );
          err.error.mensaje = message;
          this.tokenService.redirectPredefinedMailBox();
          return throwError(err);
        } else if (err.status === 403) {
          let message: string = this.translateService.instant(
            "errorsInterceptor.accessDenied2"
          );
          if (
            typeof err.error === "string" ||
            !(err.error && err.error.mensaje)
          ) {
            err = this.createError403Security();
          } else {
            err.error.mensaje = message;
            this.tokenService.redirectPredefinedMailBox();
          }
          return throwError(err);
        } else if (err.status === 0 || err.status === 503) {
          let message: string = this.translateService.instant(
            "errorsInterceptor.network"
          );
          err.error.mensaje = message;
          return throwError(err);
        } else if (err.status === 413) {
          return throwError(err);
        } else {
          return throwError(err);
        }
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set("Authorization", "Bearer " + token),
    });
  }
  private createError403Security(): HttpErrorResponse {
    let message: string = this.translateService.instant(
      "errorsInterceptor.security"
    );
    let err: HttpErrorResponse = {
      error: {
        mensaje: message,
      },
      type: HttpEventType.ResponseHeader,
      name: "HttpErrorResponse",
      message: "",
      ok: false,
      headers: new HttpHeaders(),
      status: 403,
      statusText: "",
      url: "",
    };
    return err;
  }

  private checkTokenIsExpired(): HttpErrorResponse {
    if (this.tokenService.tokenIsExpired()) {
      let message: string = this.translateService.instant(
        "errorsInterceptor.expiredToken"
      );
      let err: HttpErrorResponse = {
        error: {
          mensaje: message,
        },
        type: HttpEventType.ResponseHeader,
        name: "HttpErrorResponse",
        message: "",
        ok: false,
        headers: new HttpHeaders(),
        status: 0,
        statusText: "",
        url: "",
      };
      this.tokenService.logOut();
      return err;
    } else {
      return null;
    }
  }
}

export const interceptorProvider = [
  { provide: HTTP_INTERCEPTORS, useClass: ProdInterceptorService, multi: true },
  { provide: DEFAULT_TIMEOUT, useValue: 300000 },
];
